import { useCallback, useEffect, useState } from 'react'
import { useTheme } from '@/hooks/useTheme'
import type { AnalysisArc, AnalysisToken } from '@/types/sentence'
import { posSvgColors } from './posStyles'

interface ArcOverlayProps {
  tokens: AnalysisToken[]
  arcs: AnalysisArc[]
  activeIndex: number | null
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Notified when the chips wrap onto multiple rows — arcs are hidden then. */
  onWrappedChange?: (wrapped: boolean) => void
}

interface ChipPos {
  cx: number
  top: number
}

/** Space reserved above the chips for arcs to curve into */
export const ARC_PADDING_TOP = 120

export function ArcOverlay({ tokens, arcs, activeIndex, containerRef, onWrappedChange }: ArcOverlayProps) {
  const [chipPositions, setChipPositions] = useState<ChipPos[]>([])
  const [wrapped, setWrapped] = useState(false)
  // Read so the arc colours (sourced from CSS vars) re-resolve on theme toggle.
  useTheme()

  const measure = useCallback(() => {
    if (!containerRef.current) return
    const container = containerRef.current
    const containerBox = container.getBoundingClientRect()
    const buttons = container.querySelectorAll<HTMLElement>('[data-chip-index]')
    const positions: ChipPos[] = []
    buttons.forEach((chip) => {
      const r = chip.getBoundingClientRect()
      positions.push({
        cx: r.left - containerBox.left + r.width / 2,
        top: r.top - containerBox.top,
      })
    })
    setChipPositions(positions)
    const isWrapped =
      positions.length > 0 && positions.some((p) => Math.abs(p.top - positions[0].top) > 4)
    setWrapped(isWrapped)
    onWrappedChange?.(isWrapped)
  }, [containerRef, onWrappedChange])

  useEffect(() => {
    const raf = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    if (containerRef.current) observer.observe(containerRef.current)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure, containerRef, tokens])

  const ready = activeIndex !== null && chipPositions.length === tokens.length
  const relevantArcs = ready
    ? arcs.filter((a) => a.from === activeIndex || a.to === activeIndex)
    : []

  // Arc geometry assumes a single row of chips — hide arcs once they wrap
  // (small screens); relations stay visible via chip highlights + token detail.
  if (wrapped || relevantArcs.length === 0) return null

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
      aria-hidden="true"
    >
      <defs>
        {relevantArcs.map((arc, i) => {
          const targetPos = tokens[arc.to]?.pos ?? 'noun'
          const color = posSvgColors(targetPos).stroke
          return (
            <marker
              key={`marker-${i}`}
              id={`arc-arrow-${activeIndex}-${i}`}
              markerWidth="9"
              markerHeight="9"
              refX="7"
              refY="4.5"
              orient="auto"
            >
              <path d="M1,1 L8,4.5 L1,8 Z" fill={color} />
            </marker>
          )
        })}
      </defs>
      {relevantArcs.map((arc, i) => {
        const fromPos = chipPositions[arc.from]
        const toPos = chipPositions[arc.to]
        if (!fromPos || !toPos) return null

        const targetPos = tokens[arc.to]?.pos ?? 'noun'
        const style = posSvgColors(targetPos)

        const sx = fromPos.cx
        const sy = fromPos.top
        const ex = toPos.cx
        const ey = toPos.top

        // Arc curves upward above both endpoints; clamp so stacked arcs
        // stay inside the reserved padding when chips wrap on small screens
        const topY = Math.min(sy, ey)
        const rise = Math.max(topY - 40 - i * 26, 12)
        const mx = (sx + ex) / 2

        const d = `M${sx},${sy} Q${mx},${rise} ${ex},${ey}`
        const labelWidth = arc.label.length * 6.5 + 18
        const labelY = rise

        return (
          <g
            key={`${arc.from}-${arc.to}-${i}`}
            className="motion-safe:animate-fade-in"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            <path
              d={d}
              fill="none"
              stroke={style.stroke}
              strokeWidth="1.8"
              markerEnd={`url(#arc-arrow-${activeIndex}-${i})`}
            />
            <circle cx={sx} cy={sy} r="3" fill={style.stroke} />
            <rect
              x={mx - labelWidth / 2}
              y={labelY - 10}
              width={labelWidth}
              height={20}
              rx="10"
              fill={style.fill}
              stroke={style.stroke}
              strokeWidth="1"
            />
            <text
              x={mx}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="11"
              fontWeight="500"
              fill={style.text}
            >
              {arc.label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
